///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2025
//                                RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import Select, { components } from "react-select";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const styles = {
  multiValue: (base, state) => {
    return state.data.isFixed ? { ...base, backgroundColor: "#E6E6E6" } : base;
  },
  multiValueLabel: (base, state) => {
    return state.data.isFixed
      ? {
          ...base,
          fontWeight: "bold",
          paddingRight: 6,
          width: "97px !important"
        }
      : base;
  },
  multiValueRemove: (base, state) => {
    return state.data.isFixed ? { ...base, display: "none" } : base;
  }
};

// Custom MultiValue component to make each selected value draggable
function DraggableMultiValue(props) {
  const { data } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: data.value, disabled: data.isFixed });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    cursor: data.isFixed ? "default" : "grab"
  };

  // Prevent drag on fixed items
  const dragHandleProps = data.isFixed ? {} : listeners;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...dragHandleProps}>
      <components.MultiValue {...props} />
    </div>
  );
}

export const MultiSelect = ({ value, onChange, ...rest }) => {
  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // Only allow sorting of non-fixed items
  const sortableIds = value.map(v => v.value);

  const handleDragEnd = event => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = value.findIndex(v => v.value === active.id);
      const newIndex = value.findIndex(v => v.value === over.id);
      const newValue = arrayMove(value, oldIndex, newIndex);
      onChange(newValue);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableIds}
        strategy={verticalListSortingStrategy}
      >
        <Select
          classNamePrefix="multi-select"
          isMulti
          value={value}
          onChange={onChange}
          components={{
            MultiValue: DraggableMultiValue,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null
          }}
          closeMenuOnSelect={false}
          isClearable={false}
          isSearchable={true}
          styles={styles}
          {...rest}
        />
      </SortableContext>
    </DndContext>
  );
};

export default MultiSelect;
